import type { Dispatch, SetStateAction } from 'react'
import { useEffect, useMemo, useState } from 'react'

import { faHourglassHalf } from '@fortawesome/pro-regular-svg-icons'
import { parseISO } from 'date-fns'
import { useFormikContext } from 'formik'
import { useIntl } from 'react-intl'

import {
  useAccessTokenRoles,
  useDeliveryMethodModal,
} from '@b-stock/fe-core/bstock-next/components'
import {
  isZeroQuotePlaceholder,
  useFlag,
} from '@b-stock/fe-core/bstock-next/helpers'
import {
  activeSellerSubscriptionServiceQuery,
  getSellerCollectsConfig,
  type OrchestratedAuctionListingDetails,
  type OrchestratedQuote,
} from '@b-stock/fe-core/bstock-next/queries'
import {
  Duration,
  FormattedMessage,
  Modal,
  PrecisionNumberFormat,
  showSnackbar,
} from '@b-stock/fe-core/bstock-react'
import {
  DocumentDtoDocTypeEnum,
  ShippingTransportModeEnum,
} from '@b-stock/listing-api-client'
import { PayMethodTypeEnum } from '@b-stock/payments-methods-api-client'
import { ShipmentType } from '@b-stock/shipment-api-client'

import { CardOrder } from '@/buyer-portal/components/CardOrder'
import { ErrorBannerComponent } from '@/buyer-portal/components/ErrorBanner'
import { paysViaSellerRep } from '@/buyer-portal/components/features/listing/acceptedPaymentMethods'
import { useAuctionDetailState } from '@/buyer-portal/components/features/listing/actions/auction/useAuctionDetailState'
import {
  getContractAutoPayId,
  getHasAutoPay,
  isBidBlockedOnShippingQuote,
  isContractListing,
} from '@/buyer-portal/components/features/listing/actions/auction/utils'
import { useAnyAddressWithinRadius } from '@/buyer-portal/components/features/listing/hooks/useAnyAddressWithinRadius'
import { useCalculatedAdditionalCharges } from '@/buyer-portal/components/features/listing/hooks/useCalculatedAdditionalCharges'
import { useCostContextAnalyticsProperties } from '@/buyer-portal/components/features/listing/hooks/useCostContextAnalyticsProperties'
import { useFirstDibsPurchase } from '@/buyer-portal/components/features/listing/hooks/useFirstDibsPurchase'
import {
  getSellerBilledShippingCents,
  useSellerCollectsPaymentInfo,
} from '@/buyer-portal/components/features/listing/hooks/useSellerCollectsPaymentInfo'
import { useShippingModalTracking } from '@/buyer-portal/components/features/listing/hooks/useShippingModalTracking'
import { useSuppressShippingLine } from '@/buyer-portal/components/features/listing/hooks/useSuppressShippingLine'
import { getTlcTotals } from '@/buyer-portal/components/features/listing/hooks/useTlcTotals'
import { useTotalPaymentInfo } from '@/buyer-portal/components/features/listing/hooks/useTotalPaymentInfo'
import MaxTaxExposureDisplayContainer from '@/buyer-portal/components/features/listing/MaxTaxExposureDisplay'
import useMaxTaxExposure from '@/buyer-portal/components/features/listing/MaxTaxExposureDisplay/useMaxTaxExposure'
import MaxVatExposureDisplayContainer from '@/buyer-portal/components/features/listing/MaxVatExposureDisplay'
import getMaxVatCents from '@/buyer-portal/components/features/listing/MaxVatExposureDisplay/getMaxVatCents'
import useEuMaxVatExposure from '@/buyer-portal/components/features/listing/MaxVatExposureDisplay/useEuMaxVatExposure'
import { getSellerCollectsSellerName } from '@/buyer-portal/components/features/listing/sellerCollectsSellerName'
import { isBuyerDenied } from '@/buyer-portal/components/features/order/checkout/helpers/utils'
import { BalanceWidgetContainer } from '@/buyer-portal/components/features/order/checkout/PaymentMethods/helpers/BalanceWidgetContainer'
import { isPaymentMethodDisabled } from '@/buyer-portal/components/features/order/checkout/PaymentMethods/utils'
import { useParcelRateFlow } from '@/buyer-portal/components/ParcelRateList'
import isCanadianTaxCurrency from '@/buyer-portal/helpers/canadianTaxCurrency'
import isEuTaxCurrency from '@/buyer-portal/helpers/euTaxCurrency'
import {
  buyerPremiumAppliesFor,
  sellerCollectsListingContext,
  useSellerCollectsAnalytics,
} from '@/buyer-portal/helpers/sellerCollectsAnalytics'
import {
  feeModelHasBuyerPremium,
  getFeeModel,
} from '@/buyer-portal/helpers/sellerCollectsFeeModel'
import { getEditDeliveryMethodRedirectUri } from '@/buyer-portal/helpers/shipping'
import {
  isSellerCollectsLot,
  useIsSellerCollectsSale,
} from '@/buyer-portal/helpers/useIsSellerCollectsSale'
import { usePaymentRestrictions } from '@/buyer-portal/helpers/usePaymentRestrictions'
import useChangeCarrierModal from '@/buyer-portal/hooks/useChangeCarrierModal'
import { useParcelQuotesEnabled } from '@/buyer-portal/hooks/useParcelQuotesEnabled'
import { FeatureFlags } from '@/helpers/featureFlags'
import { GTM_PLATFORM } from '@/helpers/telemetry/constants'
import { orchestratedListingToGTMItemInput } from '@/helpers/telemetry/gtmItemAdapters'
import { getPersistedItemListContext } from '@/helpers/telemetry/itemListContext'
import { useAnalytics } from '@/helpers/telemetry/SegmentAnalytics'
import { SellerCollectsEvent } from '@/helpers/telemetry/SellerCollectsAnalyticsEvents'
import { sendTypedGTMEvent } from '@/helpers/telemetry/sendTypedGTMEvent'
import { useTrackToastView } from '@/helpers/telemetry/useTrackToastView'
import type { GTMBidIntentEvent } from '@/helpers/types/gtmTypes'

import { useAutoPayMethodContext } from '../AutoPayMethodProvider'
import { useBidFormContext } from '../BidFormContext'
import type { BidFormFormikValues } from '../BidFormFormikProvider'
import {
  AutoPayConfirmContent,
  FooterContainer,
  FooterButtonRow,
  FooterCancelButton,
  FooterConfirmButton,
} from './AutoPayConfirmContent'
import { AutoPayInfoBanner } from './AutoPayInfoBanner'
import { AutoPaymentMethodSelector } from './AutoPaymentMethodSelector'
import {
  getPaymentMethodLabel,
  PaymentMethodSelectionModal,
} from './PaymentMethodSelectionModal'
import {
  getPaymentDeadlineDays,
  bStockQuotesShipping,
  SELLER_COLLECTS_NS,
} from './sellerCollectsBidCopy'
import { getSellerCollectsFeeAccess } from './sellerCollectsFeeAccess'
import {
  findFeeCard,
  SellerCollectsFeeSection,
} from './SellerCollectsFeeSection'
import { SellerCollectsTotalNote } from './SellerCollectsSummary'
import {
  ActionContainer,
  BidAmount,
  BidDetailsContainer,
  BidDetailsContent,
  BidDetailsHeader,
  BidModal,
  ButtonRow,
  Checklist,
  ContentWrapper,
  Description,
  DetailsValue,
  Divider,
  MaxBidTitleAndBidAmount,
  QuoteLoadingSpinner,
  SlimBanner,
  StyledBanner,
  StyledButton,
  StyledCancelButton,
  StyledFontAwesomeIcon,
  StyledModalContent,
  TimeLeftSection,
  useTermsOfPurchaseLink,
} from './ui'
import { useOnBidConfirm } from './useOnBidConfirm'
import { useTrackBidConfirm } from './useTrackBidConfirm'

const ns = 'BidConfirmationModal'

export type BidConfirmationModalProps = {
  bidLocation: 'pdp' | 'list'
  buyerAccountId: string
  buyersPremium: number
  closeModal: () => void
  doNotify?: boolean
  error: unknown
  /** GLOB-4832: whether Freeze Sort is active on the originating List View —
   * see useTrackBidConfirm. Omitted entirely outside List View (e.g. LDP). */
  isSortFrozen?: boolean
  isLoading: boolean
  listing: OrchestratedAuctionListingDetails
  /** GLOB-4832/BP-2422: see useTrackBidConfirm. Omitted entirely outside List View (e.g. LDP). */
  recordBidPlacedWhileFrozen?: () => void
  selectedQuote?: OrchestratedQuote | null
  setIsPaused: Dispatch<SetStateAction<boolean>>
}

export const BidConfirmationModal = ({
  bidLocation,
  buyerAccountId,
  buyersPremium,
  closeModal,
  doNotify,
  error,
  isSortFrozen,
  isLoading,
  listing,
  recordBidPlacedWhileFrozen,
  selectedQuote,
  setIsPaused,
}: BidConfirmationModalProps) => {
  const { auction, contract } = listing
  const { actualEndTime } = auction
  const isContract = isContractListing(listing)
  const contractAutoPayId = getContractAutoPayId(listing)
  const auctionDetailState = useAuctionDetailState(listing)
  const { buyerGroupId: fdBuyerGroupId } = useFirstDibsPurchase(listing)
  const onBidConfirm = useOnBidConfirm({ doNotify })
  const trackBidConfirm = useTrackBidConfirm(
    listing,
    isSortFrozen,
    recordBidPlacedWhileFrozen
  )
  const costContext = useCostContextAnalyticsProperties({
    listing,
    selectedQuote,
  })
  const anyAddressWithinRadius = useAnyAddressWithinRadius(listing._id)
  const trackToastView = useTrackToastView()
  const formikContext = useFormikContext<BidFormFormikValues>()
  const bidAmount = formikContext.values.amount || 0
  // T184 / FR-032 — read currency from BidFormContext rather than from
  // a prop. The optional `currency` prop on this component was never
  // threaded by either render site (InternalBidFormProvider or
  // useBidConfirmationModal's MemoizedModal), so the previous default
  // of 'USD' silently shipped on every CAD/GBP/EUR listing. Reading
  // from context here closes both the visible-display sites (lines
  // ~274 / ~490 / ~600) and the GTM bid_intent analytics event.
  const {
    details: { currency },
    autoQuoteGeneration,
  } = useBidFormContext()
  const autoPayEnforced = useFlag(FeatureFlags.AUTOPAY_ENFORCED)
  const enableBalanceCreditCards = useFlag(
    FeatureFlags.ENABLE_BALANCE_CREDIT_CARDS
  )
  const contractAutoPayEnabled = useFlag(FeatureFlags.ENABLE_CONTRACT_AUTOPAY)
  const isSellerCollectsSale = useIsSellerCollectsSale(listing)
  const isParcel =
    listing.shipping?.transportMode === ShippingTransportModeEnum.Parcel
  const parcelEnabledForSeller = useParcelQuotesEnabled(listing.accountId)
  const showParcelRates = parcelEnabledForSeller && isParcel
  const autoPayEnabledForListing =
    autoPayEnforced &&
    (!isContract || (contractAutoPayEnabled && !!contractAutoPayId))
  const { trackButtonClicked } = useAnalytics()

  const { editDeliveryMethod, deliveryMethodChangePending } =
    useDeliveryMethodModal()
  const shippingTrackingCallbacks = useShippingModalTracking(listing)

  const {
    defaultPaymentMethod,
    paymentMethods,
    isSavedMethodInvalid,
    selectedPaymentMethodId,
    setSelectedPaymentMethodId,
    isWireSelected,
    setIsWireSelected,
    payableAmountInCents,
    listingIsCreditCardEnabled,
    showAchBlockedWireFallbackMessage,
  } = useAutoPayMethodContext()

  const [isPaymentMethodModalOpen, setIsPaymentMethodModalOpen] =
    useState(false)
  const [isChangeCarrierModalOpen, setIsChangeCarrierModalOpen] =
    useState(false)
  const [isChangingCarrier, setIsChangingCarrier] = useState(false)

  // Local state: FF-on initializes from context (persisted); FF-off from account default
  // If wire was previously selected, start with null (wire bypasses autoPayMethodId)
  const [autoPayMethodId, setAutoPayMethodId] = useState<string | null>(
    autoPayEnabledForListing
      ? isWireSelected
        ? null
        : (selectedPaymentMethodId ?? defaultPaymentMethod?.id ?? null)
      : (defaultPaymentMethod?.id ?? null)
  )

  useEffect(() => {
    if (autoPayEnabledForListing) {
      // FF-on: always keep autoPayMethodId in sync with context so preference changes
      // are reflected (e.g. wire initialized from a stale-then-refreshed savedAutoPay).
      // oxlint-disable-next-line react/set-state-in-effect
      setAutoPayMethodId(
        isWireSelected
          ? null
          : (selectedPaymentMethodId ?? defaultPaymentMethod?.id ?? null)
      )
    } else if (isSellerCollectsSale) {
      // Seller Collects opens the picker on this path too, so fill in the
      // default when it loads async but never overwrite the method the buyer
      // picked with Change.
      setAutoPayMethodId(
        (current) => current ?? defaultPaymentMethod?.id ?? null
      )
    } else {
      // Legacy: always sync with default when it loads async
      setAutoPayMethodId(defaultPaymentMethod?.id ?? null)
    }
  }, [
    defaultPaymentMethod?.id,
    selectedPaymentMethodId,
    autoPayEnabledForListing,
    isSellerCollectsSale,
    isWireSelected,
  ])

  const autoPayMethodLabel = useMemo(
    () => getPaymentMethodLabel(paymentMethods, autoPayMethodId),
    [autoPayMethodId, paymentMethods]
  )

  const additionalCharges = useCalculatedAdditionalCharges({
    buyersPremium,
    currentBidDollars: bidAmount,
    selectedQuote,
  })

  const { formatMessage } = useIntl()

  const selectedPaymentMethod = useMemo(
    () => paymentMethods.find((pm) => pm.id === autoPayMethodId) ?? null,
    [paymentMethods, autoPayMethodId]
  )

  const isCreditCardSelected =
    selectedPaymentMethod?.type === PayMethodTypeEnum.Card ||
    selectedPaymentMethod?.type === PayMethodTypeEnum.GetbalanceCc
  const showCcAcceptedBanner =
    enableBalanceCreditCards &&
    listingIsCreditCardEnabled &&
    !isCreditCardSelected

  const {
    ChangeCarrierModal,
    changeCarrierModalProps,
    setSelectedCarrier,
    isCarrierChangePending,
    hasMultipleCarrierOptions,
    isCheapestCarrier,
    quoteSet,
    isQuoteSetLoading,
    isQuoteSetError,
  } = useChangeCarrierModal({
    listingId: listing._id,
    originLocationId: listing.lot.location.id,
    destinationLocationId: selectedQuote?.destination.id ?? '',
    shipmentType: selectedQuote?.shipmentType ?? ShipmentType.Binding,
    currentQuote: selectedQuote ?? undefined,
    accountId: selectedQuote?.accountId ?? '',
  })

  // FP-2498 — fold Canadian max-tax exposure into Estimated Total. Shares
  // the same react-query key as `MaxTaxExposureDisplayContainer` below,
  // so no extra network round-trip.
  const isCanadianTaxApplicable = isCanadianTaxCurrency(currency) && !isContract
  const maxTaxData = useMaxTaxExposure({
    listingId: listing._id,
    formSingleBid: isCanadianTaxApplicable ? Math.round(bidAmount * 100) : 0,
    buyerAddressId: isCanadianTaxApplicable
      ? selectedQuote?.destination.id
      : undefined,
    formProxyMaxBid: isCanadianTaxApplicable
      ? Math.round(bidAmount * 100)
      : undefined,
    buyerFeeCents: isCanadianTaxApplicable
      ? additionalCharges.bstockFee
      : undefined,
    shippingCents: isCanadianTaxApplicable
      ? selectedQuote?.totalPrice
      : undefined,
    shipmentType: isCanadianTaxApplicable
      ? selectedQuote?.shipmentType
      : undefined,
  })

  // FP-3261 — fold EU/GB max-VAT exposure in the same way, so the figure
  // the buyer confirms matches the VAT-inclusive Order Total (FP-3252).
  // Shares the react-query key with `MaxVatExposureDisplayContainer`.
  const isEuVatApplicable = isEuTaxCurrency(currency) && !isContract
  const maxVatData = useEuMaxVatExposure({
    isTaxable: isEuVatApplicable,
    listingId: listing._id,
    formSingleBid: Math.round(bidAmount * 100),
    buyerAddressId: selectedQuote?.destination.id,
    formProxyMaxBid: Math.round(bidAmount * 100),
    buyerFeeCents: additionalCharges.bstockFee,
    shippingCents: selectedQuote?.totalPrice,
  })

  // Reverse-charge / unavailable estimates fold in as 0, matching the card.
  const totalTaxCents =
    (maxTaxData?.maxTaxAmount ?? 0) + getMaxVatCents(maxVatData)

  // FP-3356 — B-Stock only quotes freight it books itself, so on every other
  // Seller Collects mode a pickup quote of `totalPrice: 0` would print here
  // as "$0.00" while the fee model is still resolving.
  const suppressShippingLine = useSuppressShippingLine(listing, selectedQuote)

  const totalPaymentInfo = useTotalPaymentInfo({
    purchaseAmountDollars: bidAmount,
    selectedQuote,
    currency,
    additionalCharges,
    shipping: listing.shipping ?? null,
    isOffer: false,
    selectedPaymentMethod,
    isWireSelected,
    isCheapestCarrier,
    totalTaxCents,
    suppressShippingLine,
    // Ungated on purpose: the fee is wrong on these lots whether or not the
    // Seller Collects experience is switched on, and the flag is off in
    // production, which is exactly where buyers are seeing it.
    isSellerCollects: isSellerCollectsLot(listing),
  })

  const isZeroQuoteValue = isZeroQuotePlaceholder(selectedQuote)
  // Without a valid shipping quote we can't calculate the total, so fall
  // back to the pre-autopay (invoice later) UX even when the FF is on.
  const isShippingQuoteAvailable = !!selectedQuote && !isZeroQuoteValue

  const enforcedTotalPaymentInfo =
    autoPayEnabledForListing && isShippingQuoteAvailable && totalPaymentInfo[0]
      ? [
          {
            ...totalPaymentInfo[0],
            description: formatMessage({
              id: 'TotalPaymentInfo.estimatedTotalDescriptionAutoPay',
            }),
          },
          ...totalPaymentInfo.slice(1),
        ]
      : totalPaymentInfo

  const hasAutoPay = getHasAutoPay(listing, selectedQuote)

  const hasAnyDigitalMethods = paymentMethods.length > 0

  // TODO: refactor autopay guards (hasAutoPay, !listingIsSellerCollects) into a single variable
  // The listing's own flag, deliberately ungated: these autopay guards
  // predate the epic and must hold whether or not the buyer experience is
  // switched on. `isSellerCollectsSale` is the gated one — pick that for
  // anything the flag owns.
  const listingIsSellerCollects = listing.sellerCollects === true
  const { trackSellerCollectsEvent } = useSellerCollectsAnalytics({
    sellerAccountId: listing.accountId,
    enabled: listingIsSellerCollects,
  })
  // Only a Seller Collects lot needs the seller's plan, and only to tell a
  // fee-bearing storefront from a fee-free one.
  const {
    data: sellerSubscription,
    isLoading: isSubscriptionLoading,
    isError: isSubscriptionError,
  } = activeSellerSubscriptionServiceQuery.useQuery(listing.accountId, {
    enabled: isSellerCollectsSale,
  })
  // FP-3150 — who the buyer pays, for what, and by when. The modal is only
  // reachable signed in, so the plan is always resolvable here.
  const sellerCollectsFeeModel = getFeeModel({
    isLoggedIn: true,
    isLoading: isSubscriptionLoading,
    isError: isSubscriptionError,
    buyersPremium: sellerSubscription?.buyersPremium,
  })
  const sellerCollectsHasBuyerPremium = feeModelHasBuyerPremium(
    sellerCollectsFeeModel
  )
  // The card BCD-3 shows, which is not always the selected method: that one
  // falls back to the account default, and a default of ACH or Net Terms can
  // never be charged the fee. The picker opens on this card so it opens on
  // what the buyer just saw, rather than on a method it greys out.
  const sellerCollectsFeeCard = findFeeCard(
    paymentMethods,
    selectedPaymentMethod
  )
  // Same gate as Buy Now: while the fee is unknown the modal claims nothing,
  // so the picker and the divider wait with the section rather than arriving
  // ahead of it.
  const showSellerCollectsFeeSection =
    isSellerCollectsSale && sellerCollectsHasBuyerPremium !== undefined
  const sellerCollectsName = getSellerCollectsSellerName(listing.seller)
  const sellerCollectsConfig = getSellerCollectsConfig(listing)
  const sellerCollectsPaymentInfo = useSellerCollectsPaymentInfo({
    bidAmountDollars: bidAmount,
    bstockFeeCents: additionalCharges.bstockFee,
    currency,
    feeModel: sellerCollectsFeeModel ?? 'unknownFee',
    paymentDeadlineDays: getPaymentDeadlineDays(sellerCollectsConfig),
    sellerName: sellerCollectsName,
    sellerShippingCents: getSellerBilledShippingCents(
      bStockQuotesShipping(sellerCollectsConfig),
      selectedQuote
    ),
  })
  // Only once the fee model is known: the two models group the total
  // differently, and showing one then the other would move money on screen.
  const showSellerCollectsTotal =
    isSellerCollectsSale && sellerCollectsFeeModel !== undefined
  const isRelativeBid = contract?.bidBasis === 'RELATIVE'
  const bidUnit = contract?.bidUnit

  let maxBidAmountValue = (
    <PrecisionNumberFormat
      value={bidAmount}
      precision={auction.bidPrecision}
      currency={currency}
      format={isRelativeBid ? 'percent' : 'currency'}
      precisionStored={1}
      // Non-contract (Live Spot) amounts are plain dollars — always show
      // cents regardless of bidPrecision. Contract per-unit rates (e.g. "$25
      // per pallet") intentionally follow bidPrecision's own decimal places,
      // so this must not apply once isContract.
      forceShowCents={!isContract}
    />
  )

  // isContract can be true via contractId alone (fulfillment/AutoPay path)
  // with no populated contract.bidUnit — guard so a missing unit falls back
  // to the plain amount instead of an unresolved FormattedMessage id.
  if (isContract && bidUnit) {
    if (isRelativeBid) {
      maxBidAmountValue = (
        <FormattedMessage
          id={`${ns}.yourBidAmt.contract.${bidUnit}`}
          values={{
            amount: maxBidAmountValue,
          }}
        />
      )
    } else {
      maxBidAmountValue = (
        <>
          {maxBidAmountValue}
          <FormattedMessage id={`${ns}.yourBidAmt.contract.${bidUnit}`} />
        </>
      )
    }
  }

  const showAutoPayUI =
    autoPayEnabledForListing &&
    isShippingQuoteAvailable &&
    !listingIsSellerCollects

  // The picker normally belongs to the AutoPay flow, which Seller Collects lots
  // opt out of — but the fee section still needs it to add a card for the fee.
  const showPaymentMethodPicker =
    (showAutoPayUI || showSellerCollectsFeeSection) && isPaymentMethodModalOpen

  // A shipping quote is actively being generated and none is usable yet. Holding
  // here prevents rendering (then replacing) content derived from a not-yet-ready
  // quote — e.g. the non-autopay → autopay flash on surfaces that open the modal
  // mid-generation (BP-2263). Settles to legacy/autopay once generation finishes.
  // (Covers the initial fetch too: isLoading is true until the quote resolves.)
  // Seller-collects listings never need the resulting quote for payment, but the
  // same generation call is still what writes the backend's quote-selection
  // record for the listing; confirming before it settles can race ahead of that
  // write and 400 with "no shipment quote selected". So seller-collects still
  // holds here — it just never gets blocked below once generation settles.
  const showQuoteLoading =
    autoQuoteGeneration.isLoading && !isShippingQuoteAvailable

  const isBuyerArrangedDefault =
    listing.shipping?.allowedShipmentTypes?.[0] === 'BUYER_ARRANGED'

  // A shipping quote is required but generation settled without one (e.g. no
  // eligible address): withhold the modal entirely rather than show bid content
  // without a quote (BP-2262).
  const isActiveBuyer = useAccessTokenRoles()?.buyer === 'ACTIVE'
  const bidBlockedOnQuote = isBidBlockedOnShippingQuote({
    isActiveBuyer,
    isSellerCollects: listingIsSellerCollects,
    quoteLoading: autoQuoteGeneration.isLoading,
    selectedQuote,
  })

  // The modal opens on a bid attempt but is withheld for a missing quote; give
  // the buyer the same "missing shipping quote" toast the bid POST would have.
  // closeModal so a repeat bid attempt re-toasts.
  useEffect(() => {
    if (bidBlockedOnQuote) {
      showSnackbar({
        content: formatMessage({
          id: 'Auction.bidConfirm.errors.400.noQuoteSelected',
        }),
        options: { autoClose: true, type: 'error' },
      })
      closeModal()
    }
  }, [bidBlockedOnQuote, closeModal, formatMessage])

  // FP-1904: for parcel listings the inline rate list replaces the carrier
  // dropdown, so suppress the carrier-change affordance when showing rates.
  const shouldShowCarrierChange =
    selectedQuote?.shipmentType === ShipmentType.Binding && !showParcelRates

  // FP-1904/1906/1910: shared parcel rate-flow logic (rendering, selection, and
  // the payment-blocking states for no-rates / query error / loading).
  const {
    parcelRateList,
    parcelPaymentBlocked,
    isParcelSelectionPending,
    isParcelRatesLoading,
  } = useParcelRateFlow({
    showParcelRates,
    quoteSet,
    selectedQuote,
    isQuoteSetLoading,
    isQuoteSetError,
    isCarrierChangePending,
    setSelectedCarrier,
    currency,
  })

  const handleCarrierChange = async (quoteId: string) => {
    setIsChangeCarrierModalOpen(false)
    setIsChangingCarrier(true)
    try {
      await setSelectedCarrier(quoteId)
    } finally {
      setIsChangingCarrier(false)
    }
  }

  useEffect(() => {
    const itemInput = orchestratedListingToGTMItemInput(listing)
    const listContext = getPersistedItemListContext(listing.auction._id)
    const bidIntentEvent: GTMBidIntentEvent = {
      event: 'bid_intent',
      platform: GTM_PLATFORM,
      item_id: listing._id,
      ecommerce: {
        // T184 / FR-032 — use the listing's currency on the GTM event
        // so bid-intent analytics report CAD/GBP/EUR bids accurately
        // rather than misattributing every non-USD listing to USD.
        currency,
        items: [
          {
            ...itemInput,
            index: 0,
            quantity: 1,
            item_list_id: listContext.itemListId,
            item_list_name: listContext.itemListName,
          },
        ],
      },
    }
    sendTypedGTMEvent(bidIntentEvent)
  }, [listing, currency])

  const handleOnConfirmBidClick = () => {
    if (listingIsSellerCollects) {
      trackSellerCollectsEvent(SellerCollectsEvent.BidConfirmed, {
        ...sellerCollectsListingContext(
          listing,
          buyerPremiumAppliesFor(sellerCollectsFeeModel)
        ),
        // What was confirmed, not what the listing offers: a max bid, even on a listing that also offers Buy Now.
        saleKind: 'bid',
      })
    }
    trackBidConfirm({
      bidAmount,
      selectedQuote,
      selectedPaymentMethod,
      isWireSelected,
      anyAddressWithinRadius,
      totalLandedCostWithTax: getTlcTotals({
        additionalChargesTotal: additionalCharges.total,
        currentBidDollars: bidAmount,
        tlcTaxDollars: totalTaxCents / 100,
      }).totalLandedCostWithTax,
      currentBidUsd: costContext.properties.current_bid_usd,
      minimumRequiredBidUsd: costContext.properties.minimum_required_bid_usd,
      bstockFeeUsd: costContext.properties.bstock_fee_usd,
    })
    void onBidConfirm({
      auctionDetailState,
      amount: bidAmount,
      autoPayMethodId,
      bidLocation,
      buyerGroupId: fdBuyerGroupId,
      isWireSelected,
      closeModal,
      formikContext,
      listing,
      selectedQuote,
      setIsPaused,
      trackToastView,
    })
  }

  const handleEnforcedClose = () => {
    trackButtonClicked({ buttonName: 'cancel_bid_autocharge' })
    closeModal()
  }

  // FP-3784: the Seller Collects fee section's "Add a card" opens the credit
  // card form directly; the picker only hosts the card widget.
  const [opensOnCardForm, setOpensOnCardForm] = useState(false)

  const handlePaymentMethodClick = () => {
    setOpensOnCardForm(false)
    setIsPaymentMethodModalOpen(true)
  }

  const handleSellerCollectsAddCard = () => {
    setOpensOnCardForm(true)
    setIsPaymentMethodModalOpen(true)
  }

  // Second arg (paymentMethod) unused — only needed by autoOpenAdd auto-confirm path
  const handlePaymentMethodConfirm = (paymentMethodId: string) => {
    setAutoPayMethodId(paymentMethodId)
    if (autoPayEnabledForListing) setSelectedPaymentMethodId(paymentMethodId)
    setIsWireSelected(false)
    setIsPaymentMethodModalOpen(false)
  }

  const handleAddPaymentMethodClick = () => {
    setIsPaymentMethodModalOpen(true)
  }

  const handleWireConfirmFromModal = () => {
    setIsWireSelected(true)
    setAutoPayMethodId(null)
    if (autoPayEnabledForListing) setSelectedPaymentMethodId(null)
    setIsPaymentMethodModalOpen(false)
  }

  const handleShippingChangeClick = () => {
    editDeliveryMethod({
      ...shippingTrackingCallbacks,
      redirectURI: getEditDeliveryMethodRedirectUri(),
    })
  }

  const LegacyTermsOfPurchaseLink = useTermsOfPurchaseLink(
    listing.seller.storefront?.documents
  )

  const payRestrictions = usePaymentRestrictions()
  const sellerCollectsFeeAccess = getSellerCollectsFeeAccess({
    isSellerCollects: isSellerCollectsSale,
    enableBalanceCreditCards: enableBalanceCreditCards,
    currency,
    isAccountCardBlocked: payRestrictions.isCreditCardBlocked,
  })
  const isNonUsdCurrency = !!currency && currency.toUpperCase() !== 'USD'
  const isSelectedMethodRestricted =
    autoPayEnabledForListing &&
    selectedPaymentMethod !== null &&
    (isPaymentMethodDisabled(selectedPaymentMethod.type, {
      ach: isNonUsdCurrency || payRestrictions.isACHBlocked,
      card: isNonUsdCurrency || payRestrictions.isCreditCardBlocked,
      netTerms: isNonUsdCurrency || payRestrictions.isNetTermsBlocked,
    }) ||
      isBuyerDenied(selectedPaymentMethod))

  const hasSelectedDigitalMethod = !!selectedPaymentMethod
  // FP-1910/1906: block Confirm while a parcel rate selection is in flight, the
  // rate set is still loading, or payment is blocked (no-rates / query error) —
  // so the buyer can't submit against a rate/total that isn't settled.
  const enforcedIsConfirmDisabled =
    isLoading ||
    isSelectedMethodRestricted ||
    deliveryMethodChangePending ||
    isParcelSelectionPending ||
    isParcelRatesLoading ||
    parcelPaymentBlocked ||
    (!hasSelectedDigitalMethod && !isWireSelected)

  // Legacy content — original JSX with added autopay guards for contract/sellerCollects
  const renderLegacyContent = () => (
    <>
      <StyledModalContent>
        <ContentWrapper>
          {hasAutoPay &&
            autoPayEnabledForListing &&
            !listingIsSellerCollects && <AutoPayInfoBanner />}
          <BidDetailsContainer>
            {/* The design shows the bid back only as part of the estimated
                total: a second "Max Bid Amount" row above it reads as a
                second figure to settle. */}
            {!showSellerCollectsTotal && (
              <BidDetailsHeader>
                <MaxBidTitleAndBidAmount>
                  <span>
                    <FormattedMessage
                      id={
                        isContract
                          ? `${ns}.yourBidAmt.contract`
                          : `${ns}.yourBidAmt`
                      }
                    />
                  </span>
                  <BidAmount>{maxBidAmountValue}</BidAmount>
                </MaxBidTitleAndBidAmount>
                <Description>
                  <FormattedMessage id={`${ns}.yourBidAmt.description`} />
                </Description>
              </BidDetailsHeader>
            )}

            <BidDetailsContent>
              <div>
                {/* The generic zero-quote "no shipping fee" warning is for the
                    LTL/TL case (bid now, shipping added later). For parcel it
                    contradicts the hard block, so suppress it when the parcel
                    no-rates banner is showing. */}
                {/* Never on Seller Collects either: B-Stock adds no shipping
                    fee to this total. */}
                {isZeroQuoteValue &&
                  !parcelPaymentBlocked &&
                  !isSellerCollectsSale && (
                    <StyledBanner type="warning">
                      <FormattedMessage id={`${ns}.warning.noShippingFee`} />
                    </StyledBanner>
                  )}
                {hasAutoPay &&
                  autoPayEnabledForListing &&
                  !listingIsSellerCollects && (
                    <>
                      <AutoPaymentMethodSelector
                        onPaymentMethodClick={handlePaymentMethodClick}
                        autoPayMethodLabel={autoPayMethodLabel}
                      />
                      <Divider />
                    </>
                  )}
                {/* The section makes no claim until the premium is known, so
                    the divider waits with it rather than floating alone. */}
                {showSellerCollectsFeeSection && (
                  <>
                    <SellerCollectsFeeSection
                      hasBuyerPremium={sellerCollectsHasBuyerPremium}
                      sellerName={sellerCollectsName || undefined}
                      savedMethod={sellerCollectsFeeCard}
                      defaultMethodId={defaultPaymentMethod?.id}
                      saleKind="bid"
                      onPaymentMethodClick={handlePaymentMethodClick}
                      onAddCardClick={handleSellerCollectsAddCard}
                      canAddCard={sellerCollectsFeeAccess.canAddCard}
                      canChangeCard={sellerCollectsFeeAccess.canChangeCard}
                    />
                    <Divider data-testid="seller-collects-fee-divider" />
                  </>
                )}
                {showCcAcceptedBanner &&
                  !isSellerCollectsSale &&
                  (isZeroQuoteValue ? (
                    <SlimBanner type="info">
                      <FormattedMessage id={`${ns}.creditCardAcceptedBanner`} />
                    </SlimBanner>
                  ) : (
                    <StyledBanner type="info">
                      <FormattedMessage id={`${ns}.creditCardAcceptedBanner`} />
                    </StyledBanner>
                  ))}
                {/* FP-1910: on the parcel path, scope the polite live region
                    to just the order total so a rate switch announces the new
                    total without re-reading the whole rate list. Non-parcel
                    layout is untouched. */}
                {parcelRateList ? (
                  <>
                    {parcelRateList}
                    {!isContract && (
                      <div aria-live="polite">
                        <CardOrder
                          data={
                            showSellerCollectsTotal
                              ? sellerCollectsPaymentInfo
                              : totalPaymentInfo
                          }
                          level={1}
                          autoCollapseOnDataChange
                        />
                      </div>
                    )}
                  </>
                ) : (
                  !isContract && (
                    <CardOrder
                      data={
                        showSellerCollectsTotal
                          ? sellerCollectsPaymentInfo
                          : totalPaymentInfo
                      }
                      level={1}
                      autoCollapseOnDataChange
                    />
                  )
                )}
                {showSellerCollectsTotal &&
                  !isContract &&
                  sellerCollectsFeeModel === 'feeBearing' && (
                    <SellerCollectsTotalNote
                      sellerName={sellerCollectsName}
                      sellerInvoicesBuyer={
                        sellerCollectsConfig?.sellerInvoicesBuyer
                      }
                      paysViaRep={paysViaSellerRep(
                        sellerCollectsConfig?.acceptedPaymentMethods
                      )}
                    />
                  )}
                {/* FR-009: Contract / MAO listings defer tax to the
                    fulfilment order — max-exposure must not render here.
                    AutoPayConfirmContent carries an equivalent guard
                    (`showEstimatedTotal = !isContract || isBuyNow`). */}
                {!isContract && (
                  <MaxTaxExposureDisplayContainer
                    listingId={listing._id}
                    buyerAddressId={selectedQuote?.destination.id}
                    currencyCode={currency}
                    buyerFeeCents={additionalCharges.bstockFee}
                    shippingCents={selectedQuote?.totalPrice}
                    shipmentType={selectedQuote?.shipmentType}
                  />
                )}
                {/* FR-008: EU VAT counterpart. Same !isContract guard —
                    Contract / MAO defers to the fulfilment order.
                    Each container self-gates on currency, so at most
                    one of these two renders per listing. */}
                {!isContract && (
                  <MaxVatExposureDisplayContainer
                    listingId={listing._id}
                    buyerAddressId={selectedQuote?.destination.id}
                    currencyCode={currency}
                    buyerFeeCents={additionalCharges.bstockFee}
                    shippingCents={selectedQuote?.totalPrice}
                    shipmentType={selectedQuote?.shipmentType}
                  />
                )}
                <TimeLeftSection>
                  <div>
                    <StyledFontAwesomeIcon icon={faHourglassHalf} />
                    <FormattedMessage id={`${ns}.timeLeft`} />
                  </div>
                  {actualEndTime ? (
                    <Duration target={parseISO(actualEndTime)} format="short" />
                  ) : null}
                </TimeLeftSection>
              </div>
            </BidDetailsContent>
          </BidDetailsContainer>
          {error ? <ErrorBannerComponent error={error} /> : null}
        </ContentWrapper>
      </StyledModalContent>

      <ActionContainer>
        <Checklist>
          <DetailsValue>
            {showSellerCollectsTotal ? (
              <FormattedMessage
                id={`${SELLER_COLLECTS_NS}.termsAcknowledgement`}
                values={{ link: LegacyTermsOfPurchaseLink }}
              />
            ) : (
              <FormattedMessage
                id={`${ns}.${isContract ? 'contract' : 'check'}.agree`}
                values={{ link: LegacyTermsOfPurchaseLink }}
              />
            )}
          </DetailsValue>
        </Checklist>
        <ButtonRow $even={showSellerCollectsTotal}>
          <StyledButton
            size="medium"
            disabled={
              isLoading ||
              isParcelSelectionPending ||
              isParcelRatesLoading ||
              parcelPaymentBlocked
            }
            onClick={handleOnConfirmBidClick}
            appearance={
              isLoading || isParcelSelectionPending ? 'loading' : 'primary'
            }
          >
            <FormattedMessage id={`${ns}.action.confirm`} />
          </StyledButton>
          <StyledCancelButton
            size="medium"
            appearance={showSellerCollectsTotal ? 'secondary' : 'tertiary'}
            disabled={isLoading}
            onClick={closeModal}
          >
            <FormattedMessage id="Common.cancel" />
          </StyledCancelButton>
        </ButtonRow>
      </ActionContainer>
    </>
  )

  if (showQuoteLoading)
    return isBuyerArrangedDefault ? null : <QuoteLoadingSpinner />

  if (bidBlockedOnQuote) return null

  return (
    <>
      <BidModal
        allowContentOverflow={showAutoPayUI || showPaymentMethodPicker}
        closeModal={
          isLoading
            ? () => {
                // do nothing, prevent from closing modal
                return
              }
            : showAutoPayUI
              ? handleEnforcedClose
              : closeModal
        }
      >
        {showPaymentMethodPicker ? (
          <PaymentMethodSelectionModal
            isOpen={isPaymentMethodModalOpen}
            onClose={() => setIsPaymentMethodModalOpen(false)}
            onConfirm={handlePaymentMethodConfirm}
            accountId={buyerAccountId}
            selectedPaymentMethodId={
              showSellerCollectsFeeSection
                ? (sellerCollectsFeeCard?.id ?? autoPayMethodId)
                : autoPayMethodId
            }
            payableAmountInCents={payableAmountInCents}
            listingIsCreditCardEnabled={listingIsCreditCardEnabled}
            initialIsWireSelected={isWireSelected}
            currency={currency}
            onWireConfirm={
              hasAnyDigitalMethods ? handleWireConfirmFromModal : undefined
            }
            isSellerCollects={showSellerCollectsFeeSection}
            startWithCardForm={opensOnCardForm}
            renderActions={({ onCancel, onConfirm, isConfirmDisabled }) => (
              <FooterContainer>
                <FooterButtonRow>
                  <FooterCancelButton appearance="secondary" onClick={onCancel}>
                    <FormattedMessage
                      id={`${ns}.autoPay.modal.cancelButton.label.enforced`}
                    />
                  </FooterCancelButton>
                  <FooterConfirmButton
                    disabled={isConfirmDisabled}
                    onClick={onConfirm}
                    appearance="primary"
                  >
                    <FormattedMessage
                      id={`${ns}.autoPay.modal.confirmButton.label.enforced`}
                    />
                  </FooterConfirmButton>
                </FooterButtonRow>
              </FooterContainer>
            )}
          />
        ) : (
          <>
            <Modal.Header>
              <FormattedMessage
                id={isContract ? `${ns}.title.contract` : `${ns}.title`}
              />
            </Modal.Header>
            {showAutoPayUI ? (
              <AutoPayConfirmContent
                isACHBlocked={payRestrictions.isACHBlocked}
                currency={currency}
                listingId={listing._id}
                isContract={isContract}
                maxBidAmountValue={maxBidAmountValue}
                selectedQuote={selectedQuote}
                isZeroQuoteValue={isZeroQuoteValue}
                actualEndTime={actualEndTime}
                selectedPaymentMethod={selectedPaymentMethod}
                termsOfPurchaseUrl={
                  listing.documents?.find(
                    ({ docType }) =>
                      docType === DocumentDtoDocTypeEnum.TermsOfService
                  )?.url
                }
                isSelectedMethodRestricted={isSelectedMethodRestricted}
                isSavedMethodInvalid={isSavedMethodInvalid}
                hasAnyDigitalMethods={hasAnyDigitalMethods}
                isWireSelected={isWireSelected}
                showAchBlockedWireFallbackMessage={
                  showAchBlockedWireFallbackMessage
                }
                onWireTransferChange={() => setIsWireSelected(!isWireSelected)}
                onPaymentMethodClick={handlePaymentMethodClick}
                onAddPaymentMethodClick={handleAddPaymentMethodClick}
                onShippingChangeClick={handleShippingChangeClick}
                shippingChangePending={deliveryMethodChangePending}
                groups={listing.lot.groups}
                onCarrierChangeClick={
                  shouldShowCarrierChange
                    ? () => setIsChangeCarrierModalOpen(true)
                    : undefined
                }
                isCarrierChangeDisabled={
                  !hasMultipleCarrierOptions ||
                  isChangingCarrier ||
                  isCarrierChangePending
                }
                isCarrierChangeLoading={
                  isCarrierChangePending || isChangingCarrier
                }
                hasMultipleCarrierOptions={hasMultipleCarrierOptions}
                isCheapestCarrier={isCheapestCarrier}
                parcelRateList={parcelRateList}
                parcelPaymentBlocked={parcelPaymentBlocked}
                parcelSelectionPending={isParcelSelectionPending}
                totalPaymentInfo={enforcedTotalPaymentInfo}
                isLoading={isLoading}
                enforcedIsConfirmDisabled={enforcedIsConfirmDisabled}
                showCcAcceptedBanner={showCcAcceptedBanner}
                onConfirmBidClick={handleOnConfirmBidClick}
                onClose={handleEnforcedClose}
                error={error}
                buyerFeeCents={additionalCharges.bstockFee}
              />
            ) : (
              renderLegacyContent()
            )}
          </>
        )}
      </BidModal>

      {isChangeCarrierModalOpen && (
        <ChangeCarrierModal
          {...changeCarrierModalProps}
          onChange={handleCarrierChange}
          onClose={() => setIsChangeCarrierModalOpen(false)}
          isCarrierChangePending={isCarrierChangePending}
        />
      )}

      {/* FF autoPayEnforced: Balance SDK widget (required for GetBalance CC flow) */}
      {showAutoPayUI && !isPaymentMethodModalOpen && (
        <BalanceWidgetContainer id="balance-sdk" />
      )}

      {/* Legacy (FF off): standalone right-side panel — hidden for contracts and seller collects */}
      {hasAutoPay &&
        !autoPayEnforced &&
        !isContract &&
        !listingIsSellerCollects && (
          <PaymentMethodSelectionModal
            isOpen={isPaymentMethodModalOpen}
            onClose={() => setIsPaymentMethodModalOpen(false)}
            onConfirm={handlePaymentMethodConfirm}
            accountId={buyerAccountId}
            selectedPaymentMethodId={autoPayMethodId}
            listingIsCreditCardEnabled={listingIsCreditCardEnabled}
          />
        )}
    </>
  )
}
